import { X } from 'lucide-react'
import { useEffect, useRef, type ReactNode } from 'react'
import { IconButton } from '@/design-system/components/IconButton/IconButton'

export interface DrawerProps {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Panneau contextuel (Sprint 002/006/007) : Desktop glisse depuis la droite,
 * Mobile occupe tout l'écran. Réutilisé pour les actions rapides qui ne
 * justifient pas une page dédiée (ex. enregistrer un paiement).
 */
export function Drawer({ title, onClose, children, footer }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      // Piège de focus : sans lui, la tabulation sortait du panneau vers la
      // page derrière — sur Mobile le panneau couvre tout l'écran, le focus
      // devenait donc invisible.
      if (event.key !== 'Tab') return
      const panel = panelRef.current
      if (!panel) return
      const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (node) => node.offsetParent !== null,
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  useEffect(() => {
    // Verrou de défilement : la page continuait à défiler sous le panneau.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  return (
    <>
      <button aria-label="Fermer" className="ds-drawer-backdrop" onClick={onClose} type="button" />
      <div aria-label={title} aria-modal="true" className="ds-drawer" ref={panelRef} role="dialog">
        <header className="ds-drawer__header">
          <h2>{title}</h2>
          <IconButton icon={X} label="Fermer" onClick={onClose} />
        </header>
        <div className="ds-drawer__body">{children}</div>
        {footer ? <footer className="ds-drawer__footer">{footer}</footer> : null}
      </div>
    </>
  )
}
